import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    View
} from "react-native";
import type ITabsProps from "./type";
import type {
    TabsNavigateConstantType,
    TabsTriggerEntries,
    TabsTriggerEntry,
    TabsContextType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import TabsContent from "./components/content";
import TabsList from "./components/list";
import TabsTrigger from "./components/trigger";
import {
    TabsContext
} from "./context";
import {
    TABS_SEGMENT_MAX_TRIGGERS,
    getTabsNavigationIndex,
    getTabsOrderedValues,
    getTabsTriggerItems,
    getTabsDefaultValue,
    getTabsTabStopValue,
    getTabsSwapStep,
    getTabsKeyStep
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    warnOnce
} from "../../utils/development";

const Tabs: FC<ITabsProps> = ({
    activationMode = "automatic",
    spreadBehaviour = "baseline",
    orientation = "horizontal",
    overflowBehaviour = "auto",
    listRole = "tablist",
    isDisabled = false,
    value: valueProp,
    variant = "line",
    size = "medium",
    customLocalize,
    onValueChange,
    defaultValue,
    initialValue,
    customTheme,
    children,
    onChange,
    style,
    ...props
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const triggersRef = useRef<TabsTriggerEntries>(new Map());

    const items = getTabsTriggerItems({
        children
    });

    const [
        valueState,
        setValueState
    ] = useState<string | undefined>(defaultValue ?? initialValue);

    const isControlled = valueProp !== undefined;

    const currentValue = isControlled ? valueProp : valueState ?? getTabsDefaultValue({
        items
    });

    const previousValueRef = useRef<string | undefined>(currentValue);

    const currentActivationMode = listRole === "radiogroup" ? "automatic" : activationMode;

    const swapStep = getTabsSwapStep({
        previousValue: previousValueRef.current,
        value: currentValue,
        items
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        orientation,
        spaces
    });

    useEffect(() => {
        previousValueRef.current = currentValue;
    }, [
        currentValue
    ]);

    useEffect(() => {
        if(!isControlled || onChange || onValueChange) {
            return;
        }

        warnOnce({
            message: "Tabs: a controlled \"value\" needs an \"onChange\" (or \"onValueChange\") handler that updates it."
        });
    }, [
        onValueChange,
        isControlled,
        onChange
    ]);

    useEffect(() => {
        if(initialValue === undefined) {
            return;
        }

        warnOnce({
            message: "Tabs: \"initialValue\" is deprecated and will be removed in the next major version. Use \"defaultValue\" instead."
        });
    }, [
        initialValue
    ]);

    useEffect(() => {
        if(onChange === undefined) {
            return;
        }

        warnOnce({
            message: "Tabs: \"onChange\" is deprecated and will be removed in the next major version. Use \"onValueChange\" instead."
        });
    }, [
        onChange
    ]);

    useEffect(() => {
        if(variant !== "segment" || items.length <= TABS_SEGMENT_MAX_TRIGGERS) {
            return;
        }

        warnOnce({
            message: "Tabs: the \"segment\" variant is meant for at most 5 triggers and never scrolls. Use variant=\"line\" for longer lists."
        });
    }, [
        items.length,
        variant
    ]);

    const registerTrigger = useCallback((value: string, entry: TabsTriggerEntry) => {
        triggersRef.current.set(value, entry);

        return () => {
            if(triggersRef.current.get(value) === entry) {
                triggersRef.current.delete(value);
            }
        };
    }, []);

    const handleSelect = useStableCallback((nextValue: string) => {
        if(isDisabled || nextValue === currentValue) {
            return;
        }

        if(!isControlled) {
            setValueState(nextValue);
        }

        onValueChange?.(nextValue);

        onChange?.(nextValue);
    });

    const handleNavigate = useStableCallback(({
        value,
        key
    }: TabsNavigateConstantType) => {
        const orderedValues = getTabsOrderedValues({
            triggers: triggersRef.current,
            items
        });

        const nextValue = orderedValues[getTabsNavigationIndex({
            current: orderedValues.indexOf(value),
            count: orderedValues.length,
            step: getTabsKeyStep({
                orientation,
                direction,
                listRole,
                key
            }),
            key
        })];

        if(nextValue === undefined) {
            return false;
        }

        triggersRef.current.get(nextValue)?.ref.current?.focus();

        if(currentActivationMode === "automatic") {
            handleSelect(nextValue);
        }

        return true;
    });

    const tabStopValue = getTabsTabStopValue({
        value: currentValue,
        isDisabled,
        items
    });

    const context = useMemo<TabsContextType>(() => ({
        activationMode: currentActivationMode,
        value: currentValue,
        overflowBehaviour,
        registerTrigger,
        spreadBehaviour,
        customLocalize,
        handleNavigate,
        handleSelect,
        tabStopValue,
        customTheme,
        orientation,
        isDisabled,
        listRole,
        swapStep,
        variant,
        baseID,
        size
    }), [
        currentActivationMode,
        currentValue,
        tabStopValue,
        overflowBehaviour,
        registerTrigger,
        spreadBehaviour,
        customLocalize,
        handleNavigate,
        handleSelect,
        customTheme,
        orientation,
        isDisabled,
        listRole,
        swapStep,
        variant,
        baseID,
        size
    ]);

    return <TabsContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {children}
        </View>
    </TabsContext.Provider>;
};
export default Object.assign(Tabs, {
    Content: TabsContent,
    Trigger: TabsTrigger,
    tabsPart: "root",
    List: TabsList
});
